/**
 * Estado del tótem que viaja en cada ping de `sessions` (SOK-33).
 *
 * Usa los nombres del contrato de señal de vida de COM-16 (`network`, `screen`, `last_error`,
 * `payment_terminal`) para que, si algún día se migra a `devices/{instanceId}`, sólo cambie la
 * ruta. No suma escrituras: va en el mismo `updateDoc` del ping de 60 s.
 *
 * El estado del POS no se consulta en cada ping. Se arma con lo último que se supo: el resultado
 * de un poll o el de un pago real. Con uso alto, los pagos mantienen el dato al día. Con el tótem
 * inactivo en la pantalla inicial, se prueba el POS cada 5 min. Un poll en paralelo a un cobro
 * puede bloquear el POS (Getnet atiende una tarea a la vez), así que mientras corre la pantalla
 * queda bloqueada.
 */

import { POLLABLE_POS_TYPES } from './posPolling.js';

/** Cómo se verificó por última vez el POS. */
export const POS_CHECKED_BY = { POLL: 'poll', PAYMENT: 'payment' };

/** Tiempo mínimo entre una verificación del POS (poll o pago) y el siguiente poll por inactividad. */
export const IDLE_POS_POLL_MIN_INTERVAL_MS = 5 * 60 * 1000;

/** Sin toques ni lecturas durante este tiempo, la pantalla inicial se considera inactiva. */
export const IDLE_SCREEN_MS = 60 * 1000;

/** Cada cuánto se revisa si corresponde el poll por inactividad. */
export const IDLE_POS_POLL_TICK_MS = 30 * 1000;

/** Límite de un poll; el asistente avisa que puede tardar hasta un minuto. */
export const POS_POLL_TIMEOUT_MS = 60 * 1000;

/** Error guardado cuando el POS no responde dentro de POS_POLL_TIMEOUT_MS. */
export const POS_POLL_TIMEOUT_ERROR = 'El POS no respondió a tiempo';

/**
 * Proveedor del POS del tótem según los métodos de pago del estilo: el marcado para el asistente
 * (`useInSetupWizard`) o el primero que no sea efectivo, igual que el asistente. Sólo devuelve
 * proveedores que permiten poll.
 *
 * @param {Array<Object>} paymentMethods - `style.global.paymentMethods`.
 * @returns {string | null} `type_details` del método (`Getnet`, `MercadoPago`, `Transbank`).
 */
export function getPOSProvider(paymentMethods) {
  const posMethods = (paymentMethods || []).filter((pm) => pm && pm.type !== 'CASH');
  const method = posMethods.find((pm) => pm.useInSetupWizard === true) || posMethods[0];
  return method && POLLABLE_POS_TYPES.includes(method.type_details) ? method.type_details : null;
}

/**
 * Nuevo `payment_terminal` tras una verificación. Si el POS responde se limpia `last_error`.
 *
 * @param {Object} check
 * @param {string} check.provider
 * @param {boolean} check.ok
 * @param {'poll' | 'payment'} check.checkedBy
 * @param {Date} check.at
 * @param {string | null} [check.error]
 * @returns {{provider: string, ok: boolean, checked_by: string, last_check: Date, last_error: string | null}}
 */
export function toPaymentTerminal({ provider, ok, checkedBy, at, error = null }) {
  return {
    provider,
    ok,
    checked_by: checkedBy,
    last_check: at,
    last_error: ok ? null : (error || 'Sin conexión con el POS'),
  };
}

/**
 * Qué dejó un pago sobre el estado del POS. Sólo cuentan los pagos aprobados (el POS respondió) y
 * los fallos de comunicación. Un rechazo o un tiempo agotado no dice si el equipo está bien, así
 * que no cambia nada.
 *
 * @param {Object} payment
 * @param {boolean} payment.paid
 * @param {string | null} [payment.communicationError]
 * @returns {{ok: boolean, error: string | null} | null} `null` si el pago no informa nada.
 */
export function posCheckFromPayment({ paid, communicationError = null }) {
  if (paid) return { ok: true, error: null };
  if (communicationError) return { ok: false, error: communicationError };
  return null;
}

/**
 * Si corresponde probar el POS: hay proveedor, el tótem está en la pantalla inicial sin uso desde
 * hace IDLE_SCREEN_MS, no hay nada en curso (cobro, modal, menú secreto, otro poll) y pasó el
 * intervalo mínimo desde la última verificación, sea por poll o por pago.
 *
 * @param {Object} params
 * @param {string | null} params.provider
 * @param {Date | null} params.lastCheck
 * @param {number} params.nowMs
 * @param {number} params.lastInteractionMs
 * @param {string} params.screen - `currentView`.
 * @param {boolean} params.busy
 * @returns {boolean}
 */
export function shouldPollPOSOnIdle({ provider, lastCheck, nowMs, lastInteractionMs, screen, busy }) {
  if (!provider || busy || screen !== 'home') return false;
  if (nowMs - lastInteractionMs < IDLE_SCREEN_MS) return false;
  if (!lastCheck) return true;
  return nowMs - lastCheck.getTime() >= IDLE_POS_POLL_MIN_INTERVAL_MS;
}

/**
 * Red según el navegador. `type` (wifi, ethernet, cellular…) sólo existe en algunos navegadores;
 * si no está se usa `effectiveType` (4g, 3g…).
 *
 * @param {Navigator} nav
 * @returns {{online: boolean, type: string | null}}
 */
export function readNetwork(nav) {
  const connection = nav?.connection;
  return {
    online: nav?.onLine !== false,
    type: connection?.type || connection?.effectiveType || null,
  };
}

/**
 * Campos extra del ping de `sessions`.
 *
 * @param {Object} params
 * @param {{online: boolean, type: string | null}} params.network
 * @param {string} params.screen - `currentView`.
 * @param {{at: Date, type: string, message: string} | null} params.lastError
 * @param {Object | null} params.paymentTerminal
 * @returns {Object}
 */
export function buildSessionHealth({ network, screen, lastError, paymentTerminal }) {
  return {
    network,
    screen: screen || null,
    last_error: lastError || null,
    payment_terminal: paymentTerminal || null,
  };
}
